import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { NavLink, useNavigate } from 'react-router';
import { useAuthConfig } from '../auth';
import { useSymbolsStore } from './data';
import { listSymbols } from './api';


function getKey() {
    const d = new Date();
    const key = d.getFullYear() + '-' + d.getMonth() + '-' + d.getDate();
    return key;
}

function cacheIsUpToDate() {
    const lastUpdate = localStorage.getItem('list-updated-at');
    const key = getKey();
    return lastUpdate && lastUpdate === key;
}

function getSymbolsFromCache() {
    if (cacheIsUpToDate()) {
        return JSON.parse(localStorage.getItem('list-ref'));
    }
    return null;
}

function findSymbol(term, symbols) {
    term = term.toLowerCase();
    const res1 = symbols.filter(s => s.Symbol.toLowerCase() === term).slice(0, 10);
    const res2 = res1.length < 10 ? symbols.filter(s => s.Symbol.toLowerCase().startsWith(term)).slice(0, 10) : [];
    const res3 = res1.length + res2.length < 10 ? symbols.filter(s => s.Symbol.toLowerCase().includes(term)).slice(0, 10) : [];
    const res4 = res1.length + res2.length + res3.length < 10 ? symbols.filter(s => s.Name.toLowerCase().includes(term)).slice(0, 10) : [];
    const results = [...(new Set(res1.concat(res2, res3, res4)))].slice(0, 10);
    return results;
}


const SuggestedItem = ({ item, onSelect }) => {
    return (
        <NavLink className="block px-4 py-2 text-ink no-underline hover:bg-rust hover:text-cream" to={"/stocks/" + item.Id} onClick={onSelect}>
            <div className="flex items-baseline gap-3">
                <span className="dsp grow text-[24px] font-black">{item.Symbol}</span>
                <span className="mono text-[10px] opacity-70">{item.Market}</span>
            </div>
            <div className="truncate text-[13px]">{item.Name}</div>
        </NavLink>
    );
}

export const SymbolSearch = () => {
    const authConfig = useAuthConfig();
    const symbols = useSymbolsStore((state) => state.symbolsList);
    const setSymbols = useSymbolsStore((state) => state.setSymbolsList);

    const [isOpen, open] = useState(false);
    const [recommendations, setRecommendations] = useState([]);
    const [value, setValue] = useState('');

    const navigateTo = useNavigate();
    const input = useRef(null);

    // "/" or Ctrl/Cmd+K focuses the search from anywhere
    useEffect(() => {
        const onKey = (e) => {
            const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
            if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing)) {
                e.preventDefault();
                input.current?.focus();
            }
            if (e.key === 'Escape') {
                open(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => {
        const fetchSymbols = async () => {
            if (symbols && cacheIsUpToDate()) {
                return;
            }

            if (!symbols) {
                const syms = getSymbolsFromCache();
                if (syms) {
                    setSymbols(syms);
                    return;
                }
            }

            if (!symbols) {
                const syms = await listSymbols(authConfig);
                if (syms) {
                    localStorage.setItem('list-ref', JSON.stringify(syms));
                    const key = getKey();
                    localStorage.setItem('list-updated-at', key);
                    setSymbols(syms);
                }
            }
        };

        fetchSymbols();
    });

    const getRecommendations = (term) => {
        if (symbols) {
            return findSymbol(term, symbols);
        }
    }

    const onChange = (e) => {
        const v = e.target.value.trim();
        setValue(v);

        if (v) {
            const recs = getRecommendations(v);
            if (recs.length > 0) {
                setRecommendations(recs);
                open(true);
                return;
            }
        }

        open(false);
    }

    const onSelect = () => {
        open(false);
    }

    const onSubmit = (e) => {
        e.preventDefault();
        if (!value || value === '') {
            return;
        }
        
        if (recommendations.length === 1) {
            open(false);
            navigateTo('/stocks/' + recommendations[0].Id);
            return;
        }
        
        open(true);
    }

    return (
        <form className="relative ms-auto w-full sm:w-auto sm:min-w-[300px]" onSubmit={onSubmit} role="search">
            <label className="mono flex min-h-10 items-center gap-2.5 text-ink-inv">
                <Search className="size-4 shrink-0" />
                <input
                    ref={input}
                    name="term"
                    type="search"
                    placeholder="SEARCH A COMPANY OR TICKER"
                    aria-label="Search a company or ticker"
                    autoComplete="off"
                    onChange={onChange}
                    value={value}
                    className="min-w-0 flex-1 border-0 bg-transparent py-1.5 text-cream placeholder:text-ink-inv shadow-[inset_0_-1px_0_var(--color-slate-line)] outline-none focus:shadow-[inset_0_-1px_0_var(--color-cream)]"
                />
                <kbd className="bg-slate-hover px-1.5 py-0.5 text-[10px] text-cream">⌘K</kbd>
            </label>
            {
                isOpen &&
                <div className="absolute right-0 top-full z-50 mt-1 w-full min-w-[300px] bg-ground py-1 text-ink shadow-[0_0_0_1px_var(--color-ink),6px_6px_0_rgba(44,65,87,.35)]">
                    {recommendations.map(rec => <SuggestedItem key={rec.Id} item={rec} onSelect={onSelect} />)}
                </div>
            }
        </form>
    );
}
